import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardTitle,
  Table
} from 'react-bootstrap';
import { 
  TextField, 
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const AllPayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [ledgers, setLedgers] = useState({});

  useEffect(() => {
    fetchPayments();
    fetchLedgers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, dateFrom, dateTo, payments]);

  const fetchLedgers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/ledgers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      const ledgersData = data.data || data || [];
      const ledgerMap = {};
      ledgersData.forEach(ledger => {
        ledgerMap[ledger._id] = ledger;
      });
      setLedgers(ledgerMap);
    } catch (error) {
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${baseURL}/accounting-entries/manual/list?entryType=payment`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payments');
      }

      const paymentsList = data.data || [];
      setPayments(paymentsList);
      setFilteredPayments(paymentsList);

      const ledgerMap = { ...ledgers };
      paymentsList.forEach((p) => {
        if (p.ledger?._id) {
          ledgerMap[p.ledger._id] = p.ledger;
        }
      });
      setLedgers(ledgerMap);
    } catch (error) {
      setPayments([]);
      setFilteredPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(payment => {
        const ledger = ledgers[payment.ledgerId];
        const ledgerName = ledger ? `${ledger.firstName} ${ledger.lastName}` : '';
        return (
          ledgerName.toLowerCase().includes(term) ||
          payment.description?.toLowerCase().includes(term) ||
          payment.amount.toString().includes(term)
        );
      });
    }

    // Date filters
    if (dateFrom) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.date);
        return paymentDate >= new Date(dateFrom);
      });
    }

    if (dateTo) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.date);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        return paymentDate <= toDate;
      });
    }

    setFilteredPayments(filtered);
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedPayment(null);
  };

  const handleDelete = (payment) => {
    setPaymentToDelete(payment);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!paymentToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/accounting-entries/${paymentToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to delete payment');
      }

      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
      fetchPayments();
    } catch (error) {
      alert('Error deleting payment. Please try again.');
    }
  };

  const handleEdit = (payment) => {
    alert('Manual payments cannot be edited. Delete and create a new entry if needed.');
  };

  const getLedgerName = (payment) => {
    if (payment?.ledger) {
      return `${payment.ledger.firstName || ''} ${payment.ledger.lastName || ''}`.trim();
    }
    const ledger = ledgers[payment?.ledgerId];
    return ledger ? `${ledger.firstName} ${ledger.lastName}` : 'Unknown';
  };

  return (
    <>
      <GirviPageShell
        title="All Payments"
        subtitle="Accounting · Payments"
        chipLabel="Accounting"
        maxWidth="xl"
       compact>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                placeholder="Search by ledger name, description, or amount"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Container fluid>
            <Row>
              <Col>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Payment No</th>
                      <th>Ledger</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.length > 0 ? (
                      filteredPayments.map(payment => (
                        <tr key={payment._id}>
                          <td>{formatDate(payment.date)}</td>
                          <td>{payment.paymentNo || payment.entryNo || 'N/A'}</td>
                          <td>{getLedgerName(payment)}</td>
                          <td>₹{Number(payment.amount).toFixed(2)}</td>
                          <td>{payment.paymentMethod === 'bank' ? 'Bank' : 'Cash'}</td>
                          <td>{payment.description || 'N/A'}</td>
                          <td>
                            <IconButton
                              color="primary"
                              onClick={() => handleViewDetails(payment)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleEdit(payment)}
                              size="small"
                              disabled
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(payment)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center' }}>
                          No payments found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Container>
        )}
      </GirviPageShell>

      {/* Details Dialog */}
      <Dialog open={openDetails} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography variant="body1">{formatDate(selectedPayment.date)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Ledger</Typography>
                <Typography variant="body1">{getLedgerName(selectedPayment)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Typography variant="body1">₹{selectedPayment.amount.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                <Typography variant="body1">{selectedPayment.paymentMethod}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{selectedPayment.description || 'N/A'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllPayments;


